import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useMemo } from "react";
import { Chart } from "react-google-charts";

import { Col } from "@/components/customComponents";
import { useTheme } from "@/hooks";
import { getChartColors } from "@/pages/CreatorAnalytics/chartUtils";
import { RATING_THRESHOLDS, SCATTER_AXIS_LABELS, SCATTER_POINT } from "@/pages/CreatorAnalytics/constants";
import type { ICharacterPerformance } from "@/pages/CreatorAnalytics/types";
import { useContainerWidth } from "@/pages/CreatorAnalytics/useContainerWidth";
import { getScatterPointColor } from "@/pages/CreatorAnalytics/utils";

interface ICharacterScatterPlotProps {
  characters: ICharacterPerformance[];
}

const SCATTER_OPACITY = 0.8;
const SCATTER_AXIS_FONT_SIZE = 11;
const SCATTER_TOOLTIP_FONT_SIZE = 12;
const SCATTER_HEIGHT = "350px";

const buildScatterRow = (char: ICharacterPerformance, maxUsers: number) => {
  const tooltip = [
    char.name,
    `Requests: ${char.totalRequests.toLocaleString()}`,
    `Avg Depth: ${char.avgDepth.toFixed(1)}`,
    `Rating: ${char.avgRating !== null ? `${(char.avgRating * RATING_THRESHOLDS.PERCENTAGE_MULTIPLIER).toFixed(0)}%` : "N/A"}`,
    `Net Likes: ${char.netLikes}`,
  ].join("\n");

  const rating = char.avgRating ?? 0;
  const color = getScatterPointColor(rating);
  const sizeRatio = char.uniqueUsers / maxUsers;
  const pointSize = Math.round(SCATTER_POINT.MIN_SIZE + sizeRatio * SCATTER_POINT.SIZE_RANGE);

  return [char.totalRequests, char.avgDepth, tooltip, `point { size: ${pointSize}; fill-color: ${color}; opacity: ${SCATTER_OPACITY} }`];
};

const CharacterScatterPlot = ({ characters }: ICharacterScatterPlotProps) => {
  const { isDark } = useTheme();
  const { refContainer, width: containerWidth } = useContainerWidth();

  const { text: textColor, gridline: gridlineColor } = useMemo(() => getChartColors(isDark), [isDark]);

  const chartData = useMemo(() => {
    const header = [
      SCATTER_AXIS_LABELS.hAxisDataKey,
      SCATTER_AXIS_LABELS.vAxisDataKey,
      { role: "tooltip", type: "string" },
      { role: "style", type: "string" },
    ];
    const maxUsers = Math.max(1, ...characters.map((char) => char.uniqueUsers));
    const rows = characters.map((char) => buildScatterRow(char, maxUsers));
    return [header, ...rows];
  }, [characters]);

  const options = useMemo(
    () => ({
      hAxis: {
        title: SCATTER_AXIS_LABELS.hAxisTitle,
        textStyle: { fontSize: SCATTER_AXIS_FONT_SIZE, color: textColor },
        titleTextStyle: { color: textColor },
        gridlines: { color: gridlineColor },
        minValue: 0,
      },
      vAxis: {
        title: SCATTER_AXIS_LABELS.vAxisTitle,
        textStyle: { fontSize: SCATTER_AXIS_FONT_SIZE, color: textColor },
        titleTextStyle: { color: textColor },
        gridlines: { color: gridlineColor },
        minValue: 0,
      },
      legend: "none",
      chartArea: { width: "80%", height: "75%" },
      backgroundColor: "transparent",
      tooltip: { textStyle: { fontSize: SCATTER_TOOLTIP_FONT_SIZE }, trigger: "focus" },
    }),
    [textColor, gridlineColor],
  );

  return (
    <Col className="gap-4">
      <Col>
        <Text variant={TextVariants.HEADING_6_BOLD}>Character Map</Text>
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-default-500">
          X = requests, Y = avg depth. Color = rating (green &gt;80%, orange &gt;60%, red &lt;60%). Size = unique users.
        </Text>
      </Col>

      <div
        ref={refContainer}
        className="min-h-[300px] w-full rounded-xl border border-default-200 bg-content1 p-2 dark:border-default-100 sm:min-h-[380px] sm:p-4"
      >
        {characters.length > 0 && containerWidth > 0 ? (
          <Chart chartType="ScatterChart" width={`${containerWidth}px`} height={SCATTER_HEIGHT} data={chartData} options={options} />
        ) : (
          <Col className="flex h-[350px] items-center justify-center">
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-default-400">
              {containerWidth === 0 ? "" : "No character data available"}
            </Text>
          </Col>
        )}
      </div>
    </Col>
  );
};

export default CharacterScatterPlot;
